// Parses the academy markdown single-source (frontmatter + container
// directives) into render-ready segments. No external markdown dependency —
// special blocks become styled containers, plain segments render via
// the shared MarkdownText component.

export interface AcademyModuleMeta {
  slug: string;
  title: string;
  category: string;
  type: 'trading' | 'crypto';
  order: number;
}

export type SegmentKind = 'md' | 'summary' | 'note' | 'tip' | 'qa';

export interface AcademySegment {
  kind: SegmentKind;
  text: string;
}

export interface ParsedAcademyModule {
  meta: AcademyModuleMeta;
  segments: readonly AcademySegment[];
}

const FRONTMATTER_RE = /^---\n([\s\S]*?)\n---\n?/;
const DIRECTIVE_RE = /:::(summary|note|tip|qa)\n([\s\S]*?)\n:::/g;

function parseFrontmatter(block: string): AcademyModuleMeta {
  const get = (key: string): string => {
    const m = block.match(new RegExp(`^${key}:\\s*(.*)$`, 'm'));
    return m && m[1] ? m[1].trim() : '';
  };
  const type = get('type') === 'crypto' ? 'crypto' : 'trading';
  return {
    slug: get('slug'),
    title: get('title'),
    category: get('category'),
    type,
    order: Number.parseInt(get('order'), 10) || 0,
  };
}

export function parseAcademyMarkdown(raw: string): ParsedAcademyModule {
  const fm = raw.match(FRONTMATTER_RE);
  const meta = parseFrontmatter(fm?.[1] ?? '');
  const body = (fm ? raw.slice(fm[0].length) : raw).trim();

  const segments: AcademySegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  DIRECTIVE_RE.lastIndex = 0;
  while ((match = DIRECTIVE_RE.exec(body)) !== null) {
    const before = body.slice(lastIndex, match.index).trim();
    if (before) segments.push({ kind: 'md', text: before });
    const kind = match[1] as SegmentKind;
    const text = (match[2] ?? '').trim();
    segments.push({ kind, text });
    lastIndex = match.index + match[0].length;
  }
  const tail = body.slice(lastIndex).trim();
  if (tail) segments.push({ kind: 'md', text: tail });

  return { meta, segments };
}

/** Plain-text projection for the tutor context (strips directives + markdown). */
export function toPlainText(raw: string): string {
  const { segments } = parseAcademyMarkdown(raw);
  return segments
    .map((s) => s.text)
    .join('\n')
    .replace(/:::(summary|note|tip|qa)/g, '')
    .replace(/[#>`*_]/g, '')
    .replace(/\n{2,}/g, '\n')
    .trim();
}
